import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, User, ArrowLeft, ArrowRight, Share2, BookOpen, Sparkles } from 'lucide-react';
import axios from 'axios';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import SEO from '../components/SEO';
import Section from '../components/v2/Section';
import V2Hero from '../components/v2/V2Hero';
import { PrimaryCTA, CTABlock } from '../components/v2/CTAButton';
import { trackBlogRead } from '../utils/analytics';
import NewsletterSignup, { NewsletterModal } from '../components/v2/NewsletterSignup';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const FALLBACK_IMAGE = 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d?w=1200&h=600&fit=crop';

marked.setOptions({ breaks: true, gfm: true });

// --- Structured-data helpers (extract FAQ + HowTo from post markdown) ---
// Zero LLM cost. Deterministic. Runs on every blog post render.
function extractFAQs(md) {
  if (!md) return [];
  // Find H2 headings that are questions, capture the paragraph(s) below
  const lines = md.split('\n');
  const faqs = [];
  for (let i = 0; i < lines.length; i++) {
    const m = lines[i].match(/^##\s+(.+\?)\s*$/);
    if (!m) continue;
    const question = m[1].trim();
    let answer = '';
    for (let j = i + 1; j < lines.length; j++) {
      if (/^#{1,6}\s+/.test(lines[j])) break;
      if (lines[j].trim()) answer += (answer ? ' ' : '') + lines[j].trim();
    }
    // Strip markdown syntax from answer
    answer = answer.replace(/[*_`]/g, '').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1');
    if (answer.length > 40) faqs.push({ q: question, a: answer.slice(0, 500) });
  }
  return faqs.slice(0, 10);
}

function extractHowToSteps(title, md) {
  if (!title || !md) return null;
  // Match "5 steps", "5-Step Guide", "5 Simple Steps", "top 7 tips"
  const promise = title.match(/\b(\d+)[\s-]+(?:[a-z]+[\s-]+)?(step|tip|way|reason)s?\b/i);
  if (!promise) return null;
  const n = parseInt(promise[1], 10);
  const lines = md.split('\n');
  const steps = [];
  for (let i = 0; i < lines.length; i++) {
    const m = lines[i].match(/^##\s+(?:(?:step|tip|way)\s+)?(\d+)[.:\s)]\s*(.+?)\s*$/i);
    if (m) {
      let body = '';
      for (let j = i + 1; j < lines.length; j++) {
        if (/^#{1,6}\s+/.test(lines[j])) break;
        if (lines[j].trim()) body += (body ? ' ' : '') + lines[j].trim();
      }
      steps.push({
        name: m[2].replace(/[*_`]/g, '').trim(),
        text: body.replace(/[*_`]/g, '').replace(/\[([^\]]+)\]\([^)]+\)/g, '$1').slice(0, 400),
      });
    }
  }
  return steps.length >= Math.min(n, 3) ? { title, steps: steps.slice(0, n) } : null;
}

const parseContent = (content) => {
  if (!content) return '';
  let cleaned = content.replace(/^\s+/, '');
  cleaned = cleaned.replace(/^#\s+[^\n]*\n+/, '');
  cleaned = cleaned.replace(/^<h1[^>]*>[\s\S]*?<\/h1>\s*/i, '');
  // Only treat as raw HTML if the content STARTS with a block-level tag.
  // The old check triggered on any `<letter…>` — false-positive on posts that
  // merely *mention* HTML tags in prose (e.g. "<head>") and skipped markdown parsing.
  const startsWithHtmlBlock = /^\s*<(p|h[1-6]|div|section|article|ul|ol|blockquote|figure|table)\b/i.test(cleaned);
  return startsWithHtmlBlock ? cleaned : marked.parse(cleaned);
};

export default function BlogPost() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [copiedShare, setCopiedShare] = useState(false);

  const fetchPost = useCallback(async () => {
    setLoading(true);
    try {
      const adminToken = localStorage.getItem('adminToken');
      const response = await axios.get(`${API}/blog/${slug}`, adminToken ? { headers: { Authorization: `Bearer ${adminToken}` } } : undefined);
      // Duplicate-slug 301: if the post has a `canonical_slug`, redirect
      // to it BEFORE rendering. Google follows this JS redirect and
      // consolidates ranking signals onto the canonical URL. Also injects
      // a <link rel="canonical"> as a belt-and-braces signal in case the
      // JS redirect is delayed.
      const canon = response.data?.canonical_slug;
      if (canon && canon !== slug) {
        try {
          let linkTag = document.querySelector('link[rel="canonical"]');
          if (!linkTag) {
            linkTag = document.createElement('link');
            linkTag.setAttribute('rel', 'canonical');
            document.head.appendChild(linkTag);
          }
          linkTag.setAttribute('href', `${window.location.origin}/blog/${canon}`);
          let robotsTag = document.querySelector('meta[name="robots"]');
          if (!robotsTag) {
            robotsTag = document.createElement('meta');
            robotsTag.setAttribute('name', 'robots');
            document.head.appendChild(robotsTag);
          }
          robotsTag.setAttribute('content', 'noindex, follow');
        } catch (_) { /* ignore DOM edge cases */ }
        window.location.replace(`/blog/${canon}`);
        return;
      }
      setPost(response.data);
      trackBlogRead(
        response.data.title,
        response.data.category || 'general',
        response.data.author || 'Jo Day'
      );
    } catch (error) {
      console.error('Error fetching blog post:', error);
      setPost(null);
    } finally {
      setLoading(false);
    }
  }, [slug]);

  const fetchRelatedPosts = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/blog?limit=4`);
      const others = (response.data || []).filter((p) => p.slug !== slug).slice(0, 3);
      setRelatedPosts(others);
    } catch (error) {
      setRelatedPosts([]);
    }
  }, [slug]);

  useEffect(() => {
    fetchPost();
    fetchRelatedPosts();
  }, [fetchPost, fetchRelatedPosts]);

  const formatDate = (d) =>
    new Date(d).toLocaleDateString('en-GB', { year: 'numeric', month: 'long', day: 'numeric' });

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({ title: post.title, text: post.excerpt, url: window.location.href });
      } catch (e) {
        /* user cancelled */
      }
    } else {
      try {
        await navigator.clipboard.writeText(window.location.href);
        setCopiedShare(true);
        setTimeout(() => setCopiedShare(false), 1800);
      } catch (e) {
        /* clipboard blocked */
      }
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F6F8FB] flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#BC516F]" />
      </div>
    );
  }

  if (!post) {
    return (
      <main className="min-h-[70vh] bg-[#F6F8FB] flex items-center justify-center px-5">
        <div className="max-w-md text-center bg-white border border-[#E2E8F0] rounded-[14px] p-10 shadow-[0_10px_30px_rgba(30,42,68,0.06)]">
          <BookOpen className="w-12 h-12 text-[#94A3B8] mx-auto mb-4" />
          <h1 className="font-display font-bold text-[#1E2A44] text-2xl mb-3">Article not found</h1>
          <p className="text-[#64748B] mb-6">
            This article doesn&apos;t exist or may have moved.
          </p>
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 font-semibold text-white bg-[#BC516F] hover:bg-[#9A3A54] rounded-[10px] shadow-[0_10px_30px_rgba(188,81,111,0.25)] transition px-6 py-3"
          >
            <ArrowLeft className="w-4 h-4" /> Back to blog
          </Link>
        </div>
      </main>
    );
  }

  return (
    <main className="font-body text-[#475569] bg-[#F6F8FB]">
      <SEO
        title={post.seo_title || (`${post.title} | Audio & Co.`.length <= 60 ? `${post.title} | Audio & Co.` : post.title)}
        description={post.meta_description || post.seo_description || post.excerpt}
        keywords={post.tags || []}
        type="article"
        image={post.featured_image}
        imageAlt={post.image_alt || undefined}
        author={post.author || 'Jo Day'}
        publishedTime={post.created_at}
        modifiedTime={post.updated_at}
        noindex={Boolean(post.noindex) || post.published === false}
      />

      {/* BlogPosting Schema */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BlogPosting',
            headline: post.title,
            description: post.excerpt,
            image: {
              '@type': 'ImageObject',
              url: post.featured_image,
              width: 1200,
              height: 630,
              ...(post.image_alt ? { caption: post.image_alt, description: post.image_alt } : {}),
              ...(post.image_credit
                ? { creditText: post.image_credit, copyrightNotice: post.image_credit }
                : {}),
              license: 'https://unsplash.com/license',
              acquireLicensePage: 'https://unsplash.com/license',
            },
            author: {
              '@type': 'Person',
              name: post.author || 'Jo Day',
              url: 'https://audioandco.com/about',
              jobTitle: 'Founder',
              worksFor: { '@type': 'Organization', name: 'Audio & Co.' },
            },
            publisher: {
              '@type': 'Organization',
              name: 'Audio & Co.',
              url: 'https://audioandco.com',
              logo: {
                '@type': 'ImageObject',
                url: 'https://audioandco.com/logo.png',
                width: 600,
                height: 60,
              },
            },
            datePublished: post.created_at,
            dateModified: post.updated_at || post.created_at,
            mainEntityOfPage: {
              '@type': 'WebPage',
              '@id': `https://audioandco.com/blog/${post.slug}`,
            },
            articleSection: post.category || 'Business',
            articleBody: post.excerpt,
            wordCount: post.content?.split(/\s+/).length || 500,
            inLanguage: 'en-GB',
            isAccessibleForFree: true,
            speakable: {
              '@type': 'SpeakableSpecification',
              cssSelector: ['h1', 'h2', '[data-speakable]', '.prose p:first-of-type'],
            },
            keywords: (post.tags || []).join(', '),
            copyrightYear: new Date(post.created_at).getFullYear(),
            copyrightHolder: { '@type': 'Organization', name: 'Audio & Co.' },
          }),
        }}
      />

      {/* Breadcrumb schema */}
      <script
        type="application/ld+json"
        data-schema="breadcrumb"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://audioandco.com/' },
              { '@type': 'ListItem', position: 2, name: 'Blog', item: 'https://audioandco.com/blog' },
              { '@type': 'ListItem', position: 3, name: post.title, item: `https://audioandco.com/blog/${post.slug}` },
            ],
          }),
        }}
      />

      {/* FAQPage schema. Prefer explicit `faqs` field (populated by the admin
          FAQ-injection sweep); fall back to Q&A H2s auto-extracted from body. */}
      {(() => {
        const stored = Array.isArray(post.faqs) ? post.faqs.filter(f => f && f.q && f.a) : [];
        const faqs = stored.length >= 2 ? stored : extractFAQs(post.content);
        if (faqs.length < 2) return null;
        return (
          <script
            type="application/ld+json"
            data-schema="faq"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'FAQPage',
                mainEntity: faqs.map(f => ({
                  '@type': 'Question',
                  name: f.q,
                  acceptedAnswer: { '@type': 'Answer', text: f.a },
                })),
              }),
            }}
          />
        );
      })()}

      {/* HowTo schema (only when title promises N steps/tips and body delivers) */}
      {(() => {
        const howto = extractHowToSteps(post.title, post.content);
        if (!howto) return null;
        return (
          <script
            type="application/ld+json"
            data-schema="howto"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                '@context': 'https://schema.org',
                '@type': 'HowTo',
                name: howto.title.trim(),
                description: post.excerpt,
                image: post.featured_image,
                totalTime: 'PT10M',
                inLanguage: 'en-GB',
                step: howto.steps.map((s, i) => ({
                  '@type': 'HowToStep',
                  position: i + 1,
                  name: s.name,
                  text: s.text,
                  url: `https://audioandco.com/blog/${post.slug}#step-${i + 1}`,
                })),
              }),
            }}
          />
        );
      })()}

      {/* HERO */}
      <V2Hero
        imageUrl={post.featured_image || FALLBACK_IMAGE}
        alt={post.image_alt || post.title}
        title={post.image_title || post.title}
        overlayLeft={0.94}
        overlayMid={0.85}
        overlayRight={0.7}
        testId="blog-post-hero"
      >
        <div className="max-w-3xl">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#1E2A44] hover:text-[#BC516F] transition mb-6"
            data-testid="blog-post-back"
          >
            <ArrowLeft className="w-4 h-4" /> Back to blog
          </Link>

          <div className="flex flex-wrap gap-2 mb-5">
            {(post.tags || []).slice(0, 3).map((tag) => (
              <span
                key={tag}
                className="inline-flex items-center bg-[#BC516F]/10 text-[#BC516F] px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider"
              >
                {tag}
              </span>
            ))}
          </div>

          <h1
            className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl lg:text-5xl leading-[1.15] tracking-tight"
            data-testid="blog-post-title"
          >
            {post.title}
          </h1>

          {post.excerpt && (
            <p className="text-lg sm:text-xl text-[#475569] mt-5 leading-relaxed max-w-2xl">
              {post.excerpt}
            </p>
          )}

          <div className="flex flex-wrap items-center gap-5 mt-7 text-sm text-[#64748B]">
            <span className="inline-flex items-center gap-2">
              <Calendar className="w-4 h-4" /> {formatDate(post.created_at)}
            </span>
            <span className="inline-flex items-center gap-2">
              <User className="w-4 h-4" /> {post.author || 'Jo Day'}
            </span>
            {post.updated_at && post.updated_at !== post.created_at && (
              <span className="text-xs text-[#94A3B8]">
                Updated {formatDate(post.updated_at)}
              </span>
            )}
            <button
              type="button"
              onClick={handleShare}
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-[10px] bg-white/70 backdrop-blur-sm border border-[#E2E8F0] text-[#1E2A44] hover:bg-white transition font-semibold"
              data-testid="blog-post-share"
            >
              <Share2 className="w-3.5 h-3.5" /> {copiedShare ? 'Link copied' : 'Share'}
            </button>
          </div>
        </div>
      </V2Hero>

      {/* IMAGE CREDIT (below hero, subtle) */}
      {post.image_credit && (
        <div className="max-w-[1120px] mx-auto px-5 sm:px-6 lg:px-8 -mt-2 mb-4 text-right">
          <span
            className="text-xs text-[#94A3B8] italic"
            data-testid="blog-post-image-credit"
          >
            Photo: {post.image_credit}
          </span>
        </div>
      )}

      {/* ARTICLE BODY */}
      <Section bg="bg" testId="blog-post-body">
        <article className="max-w-3xl mx-auto">
          {/* AI-Optimised Badge */}
          <div
            className="mb-10 flex items-center gap-3 rounded-[14px] border border-[#E2E8F0] bg-white px-5 py-4 shadow-[0_4px_16px_rgba(30,42,68,0.04)]"
            data-testid="ai-citation-badge"
          >
            <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-[#BC516F]/10">
              <Sparkles className="h-4 w-4 text-[#BC516F]" />
            </div>
            <div>
              <p className="font-display text-sm font-semibold text-[#1E2A44] leading-tight">
                Written for humans. Structured for AI search.
              </p>
              <p className="text-xs text-[#64748B] leading-snug mt-0.5">
                Optimised for ChatGPT, Perplexity, Gemini &amp; Google AI Overviews. Real answers, no noise.
              </p>
            </div>
          </div>

          <div
            className="prose prose-lg max-w-none
                       prose-headings:font-display prose-headings:text-[#1E2A44] prose-headings:font-bold prose-headings:tracking-tight
                       prose-h2:text-2xl sm:prose-h2:text-3xl prose-h2:mt-12 prose-h2:mb-4
                       prose-h3:text-xl sm:prose-h3:text-2xl prose-h3:mt-8 prose-h3:mb-3
                       prose-h4:text-lg prose-h4:mt-6 prose-h4:mb-2
                       prose-p:text-[#475569] prose-p:leading-relaxed
                       prose-a:text-[#BC516F] prose-a:no-underline prose-a:font-semibold hover:prose-a:underline
                       prose-strong:text-[#1E2A44] prose-strong:font-semibold
                       prose-ul:text-[#475569] prose-ol:text-[#475569]
                       prose-li:leading-relaxed
                       prose-blockquote:border-l-4 prose-blockquote:border-[#BC516F]
                       prose-blockquote:bg-white prose-blockquote:not-italic
                       prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:rounded-r-[10px]
                       prose-blockquote:text-[#1E2A44] prose-blockquote:font-medium
                       prose-blockquote:shadow-[0_4px_16px_rgba(30,42,68,0.04)]
                       prose-img:rounded-[14px] prose-img:border prose-img:border-[#E2E8F0]
                       prose-code:text-[#1E2A44] prose-code:bg-[#F6F8FB] prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none"
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(parseContent(post.content)) }}
            data-testid="blog-post-content"
          />

          {/* Frequently asked questions (from admin FAQ-injection sweep) */}
          {Array.isArray(post.faqs) && post.faqs.filter(f => f && f.q && f.a).length >= 2 && (
            <div className="mt-16 pt-10 border-t border-[#E2E8F0]" data-testid="blog-faqs">
              <p className="text-[#BC516F] font-semibold text-sm uppercase tracking-widest">Frequently asked</p>
              <h2 className="font-display font-bold text-[#1E2A44] text-2xl sm:text-3xl tracking-tight mt-2">Quick answers.</h2>
              <div className="mt-6 space-y-4">
                {post.faqs.filter(f => f && f.q && f.a).map((f, i) => (
                  <details
                    key={i}
                    className="group bg-white border border-[#E2E8F0] rounded-[14px] shadow-[0_4px_16px_rgba(30,42,68,0.04)] overflow-hidden"
                    data-testid={`blog-faq-${i}`}
                  >
                    <summary className="cursor-pointer list-none px-6 py-4 flex items-center justify-between gap-4 font-display font-semibold text-[#1E2A44] text-[16px] leading-snug hover:bg-[#F6F8FB] transition">
                      <span>{f.q}</span>
                      <span className="text-[#BC516F] text-xl leading-none shrink-0 transition-transform group-open:rotate-45">+</span>
                    </summary>
                    <div className="px-6 pb-5 -mt-1 text-[#475569] text-[15px] leading-relaxed faq-answer" data-speakable>
                      {f.a}
                    </div>
                  </details>
                ))}
              </div>
            </div>
          )}

          {/* Author bio */}
          <div className="mt-16 pt-10 border-t border-[#E2E8F0]">
            <div className="bg-white rounded-[14px] p-7 sm:p-8 border border-[#E2E8F0] shadow-[0_4px_16px_rgba(30,42,68,0.04)]">
              <div className="flex items-start gap-5">
                <div className="w-14 h-14 shrink-0 rounded-full bg-[#BC516F]/10 flex items-center justify-center">
                  <User className="w-6 h-6 text-[#BC516F]" />
                </div>
                <div>
                  <h3 className="font-display font-semibold text-[#1E2A44] text-lg mb-1.5">
                    {post.author || 'Jo Day'}
                  </h3>
                  <p className="text-[#64748B] text-[15px] leading-relaxed">
                    Founder of Audio &amp; Co. Sixteen years helping small business owners worldwide get seen and heard.
                    Podcasts, YouTube, websites, and the SEO that makes it all work.
                  </p>
                  <Link
                    to="/about"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#BC516F] hover:text-[#9A3A54] mt-3 transition"
                  >
                    More about Jo <ArrowRight className="w-3.5 h-3.5" />
                  </Link>
                </div>
              </div>
            </div>
          </div>

          {/* Newsletter inline signup */}
          <div className="mt-10">
            <NewsletterSignup
              variant="inline"
              source="blog_inline"
              heading="Get one honest email a week."
              subtext="Practical guides like this one, straight from Jo. No fluff, no daily nagging."
              testId="blog-post-newsletter"
            />
          </div>
        </article>
      </Section>

      {/* Exit-intent modal — 20s delay per user's spec */}
      <NewsletterModal
        delayMs={20000}
        source="modal_blog"
        heading="Enjoying the read?"
        subtext="Get one honest email a week from Jo. New guides like this one, straight to your inbox."
        storageKey="nl_modal_blog_dismissed_v1"
        testId="blog-newsletter-modal"
      />

      {/* CTA */}
      <Section bg="navy" innerClassName="!py-16">
        <div className="text-center max-w-2xl mx-auto">
          <h2 className="font-display font-bold text-white text-3xl sm:text-4xl tracking-tight">
            Reading the theory is fine. Publishing weekly is what wins.
          </h2>
          <p className="text-white/75 mt-4 text-lg">
            The Pro Plan turns one recording into 12 published outputs, every week. 4-week minimum, cancel any time.
          </p>
          <div className="mt-7 flex justify-center">
            <CTABlock size="lg" align="center" invert testIdPrefix="blog-post-cta" />
          </div>
        </div>
      </Section>

      {/* RELATED */}
      {relatedPosts.length > 0 && (
        <Section bg="white" testId="blog-related">
          <div className="text-center mb-10">
            <h2 className="font-display font-bold text-[#1E2A44] text-3xl sm:text-4xl tracking-tight">
              Keep reading
            </h2>
            <p className="text-[#64748B] mt-2">Hand-picked next reads from the blog.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {relatedPosts.map((rp, i) => (
              <Link
                key={rp.id || rp.slug || i}
                to={`/blog/${rp.slug}`}
                className="group bg-white border border-[#E2E8F0] rounded-[14px] overflow-hidden shadow-[0_4px_16px_rgba(30,42,68,0.04)] hover:shadow-[0_14px_36px_rgba(30,42,68,0.10)] hover:-translate-y-0.5 transition-all"
                data-testid={`related-post-${i}`}
              >
                <div className="aspect-[16/10] overflow-hidden bg-[#F6F8FB]">
                  <img
                    src={rp.featured_image}
                    alt={rp.image_alt || rp.title}
                    title={rp.image_title || rp.title}
                    className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-500"
                    loading="lazy"
                    onError={(e) => {
                      e.target.src = FALLBACK_IMAGE;
                    }}
                  />
                </div>
                <div className="p-6">
                  {(rp.tags || []).slice(0, 1).map((tag) => (
                    <span
                      key={tag}
                      className="inline-block text-xs font-semibold uppercase tracking-wider text-[#BC516F] mb-2"
                    >
                      {tag}
                    </span>
                  ))}
                  <h3 className="font-display font-semibold text-[#1E2A44] text-lg leading-snug line-clamp-2">
                    {rp.title}
                  </h3>
                  <p className="text-[#64748B] text-sm mt-2 line-clamp-2 leading-relaxed">
                    {rp.excerpt}
                  </p>
                  <span className="inline-flex items-center gap-1 mt-4 text-sm font-semibold text-[#1E2A44] group-hover:text-[#BC516F] transition">
                    Read article <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </Section>
      )}
    </main>
  );
}
